import { useQueries, useQueryClient } from '@tanstack/react-query'
import { nativeSportsbookApi } from '@/services/api'
import { normalizeRuntimePreviewCatalog } from './nativeRuntimeCatalog'
import { EVENT_LOOKUP_BATCH, eventLookupDefinition, referencedEventIds, referencedEventQueryKey, referencedEventsQueryKey } from './nativeEventReferences'
import { isLiveSource } from './catalogSources'

// Games the screen names by id but the loaded inventory page lacks. Under live
// data they are fetched in batches through the collection preview so each carries
// the same markets, prices and regulation gating as the page; the sample
// inventory is complete, so nothing is fetched and an unknown id stays unresolved.
export function useReferencedEvents(slug, source, regulation, components, knownEvents) {
  const queryClient = useQueryClient()
  const known = new Set(knownEvents.map((event) => event.id))
  const missing = referencedEventIds(components).filter((id) => !known.has(id))
  const batches = []
  for (let start = 0; start < missing.length; start += EVENT_LOOKUP_BATCH) batches.push(missing.slice(start, start + EVENT_LOOKUP_BATCH))
  const results = useQueries({ queries: batches.map((ids) => ({
    queryKey: referencedEventsQueryKey(slug, source, regulation, ids),
    enabled: isLiveSource(source) && Boolean(slug),
    queryFn: async ({ signal }) => {
      const result = await nativeSportsbookApi.previewCollection(slug, eventLookupDefinition(ids), signal, null, regulation, source)
      if (result.state === 'unavailable') throw new Error('Games are unavailable.')
      return normalizeRuntimePreviewCatalog({ ...result, freshness: result.state === 'cached' ? 'stale' : 'replicated' }).events.filter((event) => ids.includes(event.id))
    },
    // Adding a reference re-keys its batch; keep the previous games on screen
    // while the new batch loads instead of flashing them as missing.
    placeholderData: (previous) => previous,
    staleTime: 5000, refetchInterval: isLiveSource(source) ? 15000 : false, retry: false,
  })) })
  return batches.flatMap((ids, index) => {
    const query = results[index]
    // Placeholder data is the previous batch, which may name games this batch
    // no longer references; only the current ids may reach the preview.
    const fetched = (query.data ?? []).filter((event) => ids.includes(event.id))
    if (query.isSuccess && !query.isPlaceholderData) return fetched
    // Until the batch answers, a just-picked game renders from the picker's seed.
    const seeded = ids.map((id) => queryClient.getQueryData(referencedEventQueryKey(slug, source, regulation, id))).filter(Boolean)
    return [...new Map([...seeded, ...fetched].map((event) => [event.id, event])).values()]
  })
}
